// ─── Persistent chrome ───────────────────────────────────────────────────────
//
// The frame every page sits inside: the hero with Live Status, the top pulse
// strip, the persistent module strip, and the Ask Mira bar.

import { createContext, useContext, useRef, useState, type ReactNode } from "react";
import { Card, Carousel, Chip, CloseButton } from "./primitives";
import { DictateButton } from "./dictate";
import { Metric, DataState, type LoadState } from "./state";
import { TimelineBar, type TimelineSegment } from "./timeline";

// ─── Top pulse strip ─────────────────────────────────────────────────────────

export type PulseItem = {
  id: string;
  label: string;
  /** Zero to three. The governing rule: "a tile or strip only gets a live
   *  number when that number would change what someone does next. Don't invent
   *  a third metric to fill a row of two just because other rows have three."
   *  An asymmetric strip is correct, not unfinished. */
  metrics?: { label: string; value?: number | string }[];
  onPress?: () => void;
};

export function PulseStrip({ items, state = "ready" }: {
  items: PulseItem[];
  state?: "ready" | "loading" | "unavailable";
}) {
  return (
    <div style={{ background: "var(--nl-bg-raised)", borderBottom: "1px solid var(--nl-stroke)" }}>
      {/* `items-stretch` plus `h-full` on each tile: one tile carrying two
       *  numbers must not make the one carrying a single number look
       *  unfinished beside it. The metrics stay honestly asymmetric — a tile
       *  still only shows a number when it has one — but the boxes line up. */}
      <Carousel ariaLabel="Live counts" align="stretch">
        {items.map(item => (
          <button
            key={item.id}
            type="button"
            onClick={item.onPress}
            aria-label={item.label}
            // Matched to the Home module tiles (Drea, 2026-08-17): dark-ish
            // fill, bright border. They were light for a day; the tiles moved
            // first and these follow, so the top of Home reads as one surface
            // rather than two treatments stacked.
            className="shrink-0 text-left"
            style={{
              scrollSnapAlign: "start", minWidth: 132,
              borderRadius: "var(--nl-radius)",
              background: "var(--nl-pulse-tile)",
              border: "1px solid var(--nl-module-tile-stroke)",
              boxShadow: "var(--nl-shadow), var(--nl-inner-light)",
              color: "var(--nl-ink)",
              cursor: "pointer",
              minHeight: "var(--nl-hit-min)",
            }}
          >
            <div className="flex h-full flex-col gap-1 p-3">
              <div className="text-lg tracking-wide" style={{ color: "var(--nl-ink)" }}>{item.label}</div>
              {(item.metrics ?? []).slice(0, 3).map(m => (
                <Metric key={m.label} label={m.label} value={m.value} state={state} />
              ))}
            </div>
          </button>
        ))}
      </Carousel>
    </div>
  );
}

// ─── Hero with Live Status ───────────────────────────────────────────────────

export type LiveStatusRow = { id: string; label: string; segments: TimelineSegment[] };

/** The second, smaller action beside ADD. Drea, 2026-09-19: "a + of some kind
 *  maybe add or maybe capture." Deliberately not named here — what it is
 *  differs per product, and fixing it would make every future app carry one
 *  app's answer. */
export type HeroAction = {
  label: string;
  glyph?: string;
  onPress: () => void;
  ariaLabel?: string;
};

/**
 * The hero: event title, Live Status timelines, and the ADD + control.
 *
 * Compact-by-default with expand and a View all escape hatch — the general
 * pattern the library confirms ("stay compact by default, with a simple
 * expand/collapse to reveal more, and a 'View all' escape hatch"). This is how
 * it scales from a two-field show to a large event without a redesign.
 *
 * Double-tap to expand is scoped to Live Status specifically, which is why it
 * lives here rather than in the Timeline component. Individual rows still
 * follow the global single-tap → floating viewer rule.
 */
export function Hero({
  title, rows, compactCount = 3, onAdd, onOpenRow, onViewAll, onOpenSchedule,
  logo, subtitle, secondaryAction, statusLabel = "Live status:",
  emptyMessage = "Nothing scheduled right now",
}: {
  title: string;
  /**
   * The organisation's own mark, beside the title.
   *
   * An absent logo takes no space AND renders no wrapper — the h1 stays the
   * direct child it is in Farndale, so a Farndale render is byte-identical
   * rather than merely similar. A node rather than a URL, so an app can pass
   * an <img>, an inline SVG or a glyph without this component deciding how its
   * brand is loaded.
   */
  logo?: ReactNode;
  /** A quiet second line under the title — who is signed in, and as what.
   *  Absent takes no space. */
  subtitle?: ReactNode;
  /** The second, smaller action beside ADD. Drea, 2026-09-19: "a + of some
   *  kind maybe add or maybe capture." */
  secondaryAction?: HeroAction;
  /** "Live status:" reads right for a show; a hall names the same strip
   *  differently. The furniture is identical either way. */
  statusLabel?: string;
  emptyMessage?: string;
  rows: LoadState<LiveStatusRow[]>;
  compactCount?: number;
  onAdd?: () => void;
  onOpenRow?: (id: string) => void;
  onViewAll?: () => void;
  onOpenSchedule?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  // Ref rather than a local: a render between the two taps would otherwise
  // lose the first one.
  const lastTap = useRef(0);

  const onDoubleTapArea = () => {
    const now = Date.now();
    if (now - lastTap.current < 320) setExpanded(v => !v);
    lastTap.current = now;
  };

  return (
    <header data-testid="home-hero" style={{ background: "var(--nl-bg-raised)" }}>
      <div className="flex items-start justify-between gap-4 px-4 pt-4 pb-3">
        <div className="min-w-0 flex-1">
          {/* Farndale's h1, unchanged — same element, same classes, same
              position in the tree when no logo is supplied. The logo does not
              resize it; it sits beside it, and only then is there a wrapper. */}
          {logo ? (
            <div className="flex items-center gap-3">
              <span className="nl-hero-logo flex shrink-0 items-center justify-center overflow-hidden" aria-hidden="true">
                {logo}
              </span>
              <h1 className="truncate text-3xl tracking-wide" style={{ color: "var(--nl-ink)" }}>{title}</h1>
            </div>
          ) : (
            <h1 className="truncate text-3xl tracking-wide" style={{ color: "var(--nl-ink)" }}>{title}</h1>
          )}
          {subtitle && (
            <div className="mt-1 truncate text-sm" style={{ color: "var(--nl-ink-dim)" }}>{subtitle}</div>
          )}
          <div
            className="mt-1 text-sm uppercase tracking-wide"
            style={{ color: "var(--nl-ink-dim)" }}
            onPointerDown={onDoubleTapArea}
          >
            {statusLabel}
          </div>

          <div className="mt-2 flex flex-col gap-1.5">
            <DataState state={rows} label={statusLabel} emptyMessage={emptyMessage}>
              {list => (
                <>
                  {(expanded ? list : list.slice(0, compactCount)).map(r => (
                    <TimelineBar
                      key={r.id}
                      leadLabel={r.label}
                      segments={r.segments}
                      onOpen={() => onOpenRow?.(r.id)}
                      compact
                    />
                  ))}
                  {list.length > compactCount && (
                    <div className="mt-1 flex items-center gap-3">
                      <button
                        type="button" onClick={() => setExpanded(v => !v)}
                        className="text-xs uppercase tracking-wide"
                        style={{ color: "var(--nl-ink-dim)", background: "none", border: 0, cursor: "pointer" }}
                      >
                        {expanded ? "Collapse" : `Expand (${list.length - compactCount} more)`}
                      </button>
                      {onViewAll && (
                        <button
                          type="button" onClick={onViewAll}
                          className="text-xs uppercase tracking-wide"
                          style={{ color: "var(--nl-ink-dim)", background: "none", border: 0, cursor: "pointer" }}
                        >
                          View all
                        </button>
                      )}
                      {/* "An expanded Live Status needs its own explicit
                       *  Schedule button — that's what opens the full Schedule
                       *  page, not tapping a location row." */}
                      {expanded && onOpenSchedule && (
                        <button
                          type="button" onClick={onOpenSchedule}
                          className="text-xs uppercase tracking-wide"
                          style={{ color: "var(--nl-ink)", background: "none", border: 0, cursor: "pointer" }}
                        >
                          Schedule
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </DataState>
          </div>
        </div>

        {secondaryAction ? (
          <div className="flex shrink-0 items-stretch gap-2">
            {onAdd && <AddButton onAdd={onAdd} />}
            {/* Smaller, and on the module-tile treatment rather than the pale
                band, so it reads as the second action rather than a rival to
                ADD. Same height, so the pair is one block. */}
            <button
              type="button"
              onClick={secondaryAction.onPress}
              aria-label={secondaryAction.ariaLabel ?? secondaryAction.label}
              data-testid="home-hero-secondary-action"
              className="flex shrink-0 flex-col items-center justify-center px-3 py-3"
              style={{
                borderRadius: "var(--nl-radius)", background: "var(--nl-pulse-tile)",
                border: "1px solid var(--nl-module-tile-stroke)",
                boxShadow: "var(--nl-shadow), var(--nl-inner-light)",
                color: "var(--nl-ink)", cursor: "pointer", minWidth: 64,
              }}
            >
              <span className="text-xs uppercase tracking-wide">{secondaryAction.label}</span>
              <span className="text-2xl leading-none">{secondaryAction.glyph ?? "+"}</span>
            </button>
          </div>
        ) : onAdd ? <AddButton onAdd={onAdd} /> : null}
      </div>
    </header>
  );
}

/** Farndale's ADD control, lifted into its own function purely so the
 *  with-secondary and without-secondary branches above render the SAME markup
 *  rather than two copies that can drift. Byte-for-byte Farndale's. */
function AddButton({ onAdd }: { onAdd: () => void }) {
  return (
      <button
        type="button" onClick={onAdd} aria-label="Add"
        data-testid="home-hero-action"
        /* `nl-light-card` re-inks the subtree, so the label is dark on the
           pale ground rather than pale-on-pale. */
        className="nl-light-card flex shrink-0 flex-col items-center justify-center px-5 py-3"
        style={{
          borderRadius: "var(--nl-radius)", background: "var(--nl-band)",
          border: "1px solid var(--nl-band-stroke)", boxShadow: "var(--nl-shadow)",
          cursor: "pointer", minWidth: 96,
        }}
      >
        <span className="text-lg tracking-wide">ADD</span>
        <span className="text-3xl leading-none">+</span>
      </button>
  );
}

// ─── Persistent module strip ─────────────────────────────────────────────────

export type ModuleItem = {
  id: string;
  label: string;
  onPress?: () => void;
  active?: boolean;
  /**
   * `alert` marks a destination that is not locked down yet — today, the
   * entries admin workspace (Drea, 2026-08-18). The badge ON that page was not
   * enough: what needs to stand out is the WAY IN, so somebody can find it and
   * so nobody wanders into it without noticing what it is.
   */
  tone?: "alert";
};

/**
 * "Home's large navigation tiles collapse into a compact, persistent
 * horizontal strip at the bottom of the screen once a user is inside a
 * module … Configurable per the Home/module configuration already specified,
 * not hard-coded to whichever modules happen to be visible in any one mockup."
 *
 * Hence `items` is a prop with no default list. Which modules exist is
 * configuration (register item #5), not something this component decides.
 */
export function ModuleStrip({ items }: { items: ModuleItem[] }) {
  return (
    <nav aria-label="Modules" style={{ background: "var(--nl-bg-raised)", borderTop: "1px solid var(--nl-stroke)" }}>
      <Carousel ariaLabel="Modules">
        {items.map(m => (
          <button
            key={m.id}
            type="button"
            onClick={m.onPress}
            aria-current={m.active ? "page" : undefined}
            className="shrink-0 px-4 py-3 text-sm uppercase tracking-wide"
            style={{
              scrollSnapAlign: "start",
              borderRadius: "var(--nl-radius-sm)",
              background: m.tone === "alert" ? "var(--nl-notice-problem-bg)"
                : m.active ? "var(--nl-tile-hover)" : "var(--nl-tile)",
              border: `1px solid ${m.tone === "alert" ? "var(--nl-status-attention)"
                : m.active ? "var(--nl-stroke-strong)" : "var(--nl-stroke)"}`,
              color: m.tone === "alert" ? "var(--nl-status-attention)" : "var(--nl-ink)",
              cursor: "pointer", minHeight: "var(--nl-hit-min)",
            }}
          >
            {m.label}
          </button>
        ))}
      </Carousel>
    </nav>
  );
}

// ─── Ask Mira ────────────────────────────────────────────────────────────────

export type AskMiraMode = "type" | "dictate" | "talk";

/**
 * "Pressing 'Ask Mira' slides a panel up from the bottom of the screen — one
 * input area ('Ask Mira anything…') with three controls beside it: TYPE ·
 * DICTATE · TALK."
 *
 * `voiceAvailable` is the load-bearing prop. "When connectivity can't support
 * full voice (a real, expected field condition — patchy wifi from a generator
 * or a farmer's router, not a hypothetical edge case), TALK simply greys out
 * or disappears; TYPE and DICTATE remain, and the interface needs no redesign
 * for that state."
 *
 * So degradation is a normal prop with a normal rendering, not an error path.
 * The panel looks deliberate either way.
 */
export function AskMiraBar({ onOpen, label = "Ask Mira AI", unavailableReason }: {
  onOpen: () => void;
  /** Overrides "Ask Mira AI" where an app's assistant has another name. */
  label?: string;
  /**
   * Why this app has no assistant yet, in words.
   *
   * The bar is furniture — it is on every home screen in this design language
   * and removing it would make one app's home a different shape from the rest.
   * But a button opening a panel connected to nothing is the exact failure
   * this platform was built against, so an app with no assistant says so and
   * cannot press it.
   */
  unavailableReason?: string;
}) {
  if (unavailableReason) {
    return (
      <div className="sticky bottom-0 z-40 px-4 py-3" style={{ background: "var(--nl-band)" }}>
        {/* DataState's own "unavailable" row — leading glyph, dim ink, plain
            words — on the bar's normal surface. Not a new treatment: the one
            this design system already uses everywhere something is absent. */}
        <div
          className="flex w-full items-center justify-center gap-2.5 py-3 text-sm"
          style={{
            borderRadius: "var(--nl-radius-sm)",
            background: "var(--nl-panel)", color: "var(--nl-ink-dim)",
            border: "1px solid var(--nl-stroke)",
            minHeight: "var(--nl-hit-min)",
          }}
        >
          <span aria-hidden className="text-base">⚠</span>
          <span>{unavailableReason}</span>
        </div>
      </div>
    );
  }
  return (
    // Sticky rather than sitting at the end of the page (Drea, 2026-08-16):
    // on a long list — three hundred entries, a full duty board — it was
    // scrolled off and effectively did not exist. Sticky keeps it in reach
    // and, unlike fixed, still occupies its own space so nothing hides under
    // it. `bottom: 0` with the frame's normal flow does the rest.
    <div
      className="sticky bottom-0 z-40 px-4 py-3"
      style={{ background: "var(--nl-band)" }}
    >
      {/* "Ask Mira AI", not "ASK MIRA" (Drea, 2026-08-22). People who have
          never heard of Mira are using this now, and a name on its own tells
          them nothing about what the button does.

          The height is LOAD-BEARING: this bar sits directly beneath the module
          strip and the two read as one piece of furniture, so the label must
          not grow the box. It does not — "Ask Mira AI" is three characters
          longer and still one line at every width the app supports, verified
          rather than assumed. `whitespace-nowrap` makes that a guarantee
          instead of a coincidence at some future narrower screen: the text
          would ellipsize before it would wrap and push the bar taller. */}
      <button
        type="button" onClick={onOpen}
        className="w-full truncate whitespace-nowrap py-3 text-xl tracking-wide"
        style={{
          borderRadius: "var(--nl-radius-sm)",
          background: "var(--nl-panel)", color: "var(--nl-ink)",
          border: "1px solid var(--nl-stroke-strong)", boxShadow: "var(--nl-shadow)", cursor: "pointer",
          minHeight: "var(--nl-hit-min)",
        }}
      >
        {label}
      </button>
    </div>
  );
}

/**
 * What an answer looks like to the interface.
 *
 * `sources` and `grounded` are not decoration. The product's founding failure
 * was an assistant reporting work it had never done, so an answer arrives with
 * what was actually looked at, and an answer resting on no lookup at all says
 * so on screen rather than reading like every other reply.
 */
export type AskMiraAnswer = {
  answer: string;
  sources: { looked: string; found: number }[];
  grounded: boolean;
  truncated: boolean;
};

export type AskMiraHandler = (question: string, mode: AskMiraMode) => Promise<AskMiraAnswer>;

/**
 * Live voice, as the panel sees it.
 *
 * The panel drives it and renders it; it owns none of it. `available` is a
 * real check (see useVoiceAvailability) rather than the hopeful default this
 * prop used to carry — TALK was always offered and then failed in precisely
 * the field conditions the design language describes it degrading for.
 */
export type AskMiraTalk = {
  available: boolean;
  /** Why voice is off, in words somebody standing in a field can act on. */
  unavailableReason: string | null;
  status: "idle" | "connecting" | "listening" | "sending" | "thinking" | "speaking" | "reconnecting" | "error";
  /** What it heard you say, as Whisper returns it. */
  transcript: string;
  /** What it said back, in text as well as speech. */
  answer: string;
  /** What that answer was checked against — a spoken reply cannot show its
   *  working, so the panel does. */
  sources: { looked: string; found: number }[];
  grounded: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
};

/**
 * How the answering machinery reaches the panel.
 *
 * A plain function, deliberately: this file is the component library, which
 * "doesn't fetch data or know about the object model". A context carrying one
 * async function keeps that true while letting every screen in the product get
 * a working Ask Mira without seven copies of the same wiring — the same "one
 * shell, one answer" reasoning that moved the Home button into AppFrame.
 */
export const AskMiraContext = createContext<AskMiraHandler | null>(null);

/** Live voice, supplied the same way and for the same reason. */
export const AskMiraTalkContext = createContext<AskMiraTalk | null>(null);

export function AskMiraPanel({
  open, onClose, onSubmit, voiceAvailable = true, placeholder = "Ask Mira anything…",
}: {
  open: boolean;
  onClose: () => void;
  /** Only used when no handler is in context — the design gallery's case. */
  onSubmit?: (text: string, mode: AskMiraMode) => void;
  voiceAvailable?: boolean;
  placeholder?: string;
}) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState<AskMiraMode>("type");
  const handler = useContext(AskMiraContext);
  const talk = useContext(AskMiraTalkContext);
  const [asked, setAsked] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [answer, setAnswer] = useState<AskMiraAnswer | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const send = async () => {
    const question = text.trim();
    if (!question) return;
    if (!handler) { onSubmit?.(question, mode); setText(""); onClose(); return; }
    setAsked(question); setText(""); setAnswer(null); setFailed(null); setPending(true);
    try {
      setAnswer(await handler(question, mode));
    } catch (err) {
      // Says it failed. An assistant that quietly answers nothing looks
      // identical to one that has nothing to say.
      setFailed(err instanceof Error ? err.message : "That didn’t reach the server.");
    } finally {
      setPending(false);
    }
  };

  const close = () => {
    setAsked(null); setAnswer(null); setFailed(null); onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "var(--nl-scrim)" }}
      onClick={close}
      role="dialog" aria-modal="true" aria-label="Ask Mira"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-2xl p-4"
        style={{
          background: "var(--nl-bg-raised)",
          borderRadius: "var(--nl-radius-lg) var(--nl-radius-lg) 0 0",
          border: "1px solid var(--nl-stroke)", boxShadow: "var(--nl-shadow)",
          maxHeight: "85vh", overflowY: "auto",
        }}
      >
        <div className="mb-3 flex items-center justify-between">
          <span className="text-lg" style={{ color: "var(--nl-ink)" }}>Ask Mira</span>
          <CloseButton onPress={close} />
        </div>

        {/* ── A live voice turn ───────────────────────────────────────────
            Same answer as the typed path, from the same tools, with the same
            "what was this checked against" line — a spoken reply cannot show
            its working, so the panel shows it while it talks. */}
        {talk && talk.status !== "idle" && (
          <div
            className="mb-3 flex flex-col gap-2 px-3 py-3"
            style={{ borderRadius: "var(--nl-radius-sm)", background: "var(--nl-tile)", border: "1px solid var(--nl-stroke)" }}
          >
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                style={{
                  // A status dot stays round: it is an indicator, not one of
                  // the capsule tabs the squaring was aimed at.
                  width: 10, height: 10, borderRadius: 999,
                  background: talk.status === "listening" ? "var(--nl-status-on)"
                    : talk.status === "speaking" ? "var(--nl-status-available)"
                    : talk.status === "error" ? "var(--nl-status-attention)"
                    : "var(--nl-ink-faint)",
                }}
              />
              <span className="text-sm uppercase tracking-wide" style={{ color: "var(--nl-ink-dim)" }}>
                {talk.status === "connecting" ? "Connecting…"
                  : talk.status === "reconnecting" ? "Reconnecting…"
                  : talk.status === "listening" ? "Listening"
                  : talk.status === "thinking" ? "Looking it up…"
                  : talk.status === "speaking" ? "Speaking"
                  : talk.status === "sending" ? "Sending…"
                  : "Voice stopped"}
              </span>
              <button
                type="button" onClick={talk.stop}
                className="ml-auto text-xs uppercase"
                style={{ color: "var(--nl-ink-faint)", background: "none", border: 0, cursor: "pointer" }}
              >Stop</button>
            </div>

            {talk.transcript && (
              <p className="text-sm" style={{ color: "var(--nl-ink-faint)" }}>You said: {talk.transcript}</p>
            )}
            {talk.answer && (
              <p className="whitespace-pre-wrap text-base" style={{ color: "var(--nl-ink)" }}>{talk.answer}</p>
            )}
            {talk.sources.length > 0 && (
              <ul className="flex flex-col gap-0.5">
                {talk.sources.map((s, i) => (
                  <li key={i} className="text-xs" style={{ color: "var(--nl-ink-faint)" }}>
                    Checked {s.looked} — {s.found} found
                  </li>
                ))}
              </ul>
            )}
            {talk.answer && !talk.grounded && (
              <p className="text-xs" style={{ color: "var(--nl-status-attention)" }}>
                That answer wasn’t checked against your records.
              </p>
            )}
            {talk.error && (
              <p className="text-xs" style={{ color: "var(--nl-status-attention)" }}>{talk.error}</p>
            )}
          </div>
        )}

        {asked && (
          <div className="mb-3 flex flex-col gap-2">
            <p className="text-sm" style={{ color: "var(--nl-ink-faint)" }}>You asked: {asked}</p>

            {pending && (
              <div
                className="px-3 py-3 text-base"
                style={{ borderRadius: "var(--nl-radius-sm)", background: "var(--nl-tile)", color: "var(--nl-ink-dim)" }}
              >
                Looking it up…
              </div>
            )}

            {failed && (
              <div
                className="px-3 py-3 text-base"
                style={{ borderRadius: "var(--nl-radius-sm)", background: "var(--nl-tile)", color: "var(--nl-status-attention)" }}
              >
                {failed}
              </div>
            )}

            {answer && (
              <div
                className="px-3 py-3"
                style={{ borderRadius: "var(--nl-radius-sm)", background: "var(--nl-tile)", border: "1px solid var(--nl-stroke)" }}
              >
                <p className="whitespace-pre-wrap text-base" style={{ color: "var(--nl-ink)" }}>{answer.answer}</p>

                {/* What it actually looked at. A person can check this; a
                    confident sentence on its own cannot be checked at all. */}
                {answer.sources.length > 0 && (
                  <ul className="mt-3 flex flex-col gap-0.5">
                    {answer.sources.map((s, i) => (
                      <li key={i} className="text-xs" style={{ color: "var(--nl-ink-faint)" }}>
                        Checked {s.looked} — {s.found} found
                      </li>
                    ))}
                  </ul>
                )}

                {!answer.grounded && (
                  <p className="mt-3 text-xs" style={{ color: "var(--nl-status-attention)" }}>
                    This answer wasn’t checked against your records.
                  </p>
                )}
                {answer.truncated && (
                  <p className="mt-1 text-xs" style={{ color: "var(--nl-status-attention)" }}>
                    It ran out of steps, so this may be incomplete.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full resize-none p-3 text-base"
          style={{
            borderRadius: "var(--nl-radius-sm)", background: "var(--nl-bg)",
            color: "var(--nl-ink)", border: "1px solid var(--nl-stroke)",
          }}
        />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Chip selected={mode === "type"} onPress={() => setMode("type")}>TYPE</Chip>
          {/* DICTATE is now a working control rather than a mode label —
              V1 priority 9 gives Ask Mira the same mic as messaging. */}
          <DictateButton label="Dictate" onText={t => { setMode("dictate"); setText(v => (v ? `${v} ${t}` : t)); }} />
          {/* TALK is now the control that starts and stops a live session,
              not a label that sets a mode nothing reads. Availability is a
              real check when one is supplied — see useVoiceAvailability. */}
          {(talk ? talk.available : voiceAvailable) ? (
            <Chip
              selected={mode === "talk"}
              onPress={() => {
                setMode("talk");
                if (!talk) return;
                talk.status === "idle" || talk.status === "error" ? talk.start() : talk.stop();
              }}
            >
              {talk && talk.status !== "idle" && talk.status !== "error" ? "STOP" : "TALK"}
            </Chip>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm"
              style={{ borderRadius: "var(--nl-pill)", border: "1px dashed var(--nl-stroke)", color: "var(--nl-ink-faint)" }}
              title={talk?.unavailableReason ?? "Live voice needs a stronger connection"}
            >
              TALK
            </span>
          )}

          <button
            type="button"
            onClick={send}
            disabled={!text.trim() || pending}
            className="ml-auto px-5 py-2.5 text-sm uppercase tracking-wide"
            style={{
              // Brighter (Drea, 2026-08-16). `--nl-fill` is the timeline's
              // muted sage and read as disabled next to the pale panel; send
              // is the one control in this panel that does something.
              borderRadius: "var(--nl-radius-sm)", background: "var(--nl-send)",
              color: "var(--nl-send-ink)", border: 0,
              fontWeight: 600,
              cursor: pending ? "progress" : "pointer",
              opacity: !text.trim() || pending ? 0.45 : 1,
              minHeight: "var(--nl-hit-min)",
            }}
          >
            {pending ? "Asking…" : "Send"}
          </button>
        </div>

        {(talk ? !talk.available : !voiceAvailable) && (
          <p className="mt-2 text-xs" style={{ color: "var(--nl-ink-faint)" }}>
            {/* The two failures are different problems with different answers.
                Telling somebody the signal is weak when their browser simply
                cannot record sends them to stand in the wrong place. */}
            {talk?.unavailableReason ?? "Live voice needs a stronger connection."} Type and dictate still work.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Page frame ──────────────────────────────────────────────────────────────

/** Composes the persistent chrome so a page only supplies its own middle. */
/**
 * Go back, and land somewhere real if there is no back to go to.
 *
 * `window.history.back()` on a page opened DIRECTLY — an invitation link, a
 * shared URL, a home-screen shortcut into a profile — steps out of the app
 * entirely and leaves a blank tab. The person is not "back", they are
 * nowhere, and nothing on screen explains it.
 *
 * So: use real history when this app put something there, and otherwise
 * navigate to a sensible parent. The ref is what makes that answerable —
 * `history.length` counts the whole tab's history including pages that were
 * never ours, so it cannot tell us whether WE have anywhere to return to.
 */
export function useSafeBack(fallback: string, navigate: (path: string) => void): () => void {
  const startedAt = useRef(typeof window === "undefined" ? 0 : window.history.length);

  return () => {
    if (typeof window !== "undefined" && window.history.length > startedAt.current) {
      window.history.back();
      return;
    }
    navigate(fallback);
  };
}

export function AppFrame({
  hero, pulse, modules, children, onAskMira, voiceAvailable, onHome, onBack, title, banner,
  updateBanner, theme = "farndale", assistantLabel, assistantUnavailable,
}: {
  hero?: ReactNode;
  /** A strip above everything — used to say plainly when the page is showing
   *  a preview rather than the present. */
  banner?: ReactNode;
  pulse?: ReactNode;
  modules?: ReactNode;
  children: ReactNode;
  onAskMira?: (text: string, mode: AskMiraMode) => void;
  voiceAvailable?: boolean;
  /**
   * Back to Home. Every module page passes this; Home itself does not, which
   * is the only reason it is optional.
   *
   * It lives in the frame rather than in each page because the pages that had
   * it and the pages that did not were the same product to the person using
   * it — Drea found Schedule could get her home and Tasks and Stewards simply
   * could not. One shell, one answer.
   */
  onHome?: () => void;
  /**
   * Back one step, as the browser understands it.
   *
   * Drea, 2026-08-17: inside Judging the only way out threw her to Home, "a
   * long way back to Judging from there", and a person's profile had no way
   * out at all. Home is a destination, not a back button — it answers "where
   * do I live" and never "undo that tap".
   *
   * `history.back()` rather than a hard-coded parent, because the same screen
   * is reached from several places: an entrant's record is opened from the day
   * list and from the admin table, and a profile from People, a task, a group
   * or a message. A fixed parent is right for exactly one of those journeys
   * and wrong for the rest.
   */
  onBack?: () => void;
  title?: string;
  /** Farndale renders `<UpdateBanner />` here. It reads a per-app
   *  service-worker channel, which is app infrastructure rather than shell
   *  layout, so the frame takes the rendered node instead of importing it.
   *  Renders nothing until an update is actually waiting, in Farndale and
   *  here alike — the position in the tree is unchanged. */
  updateBanner?: ReactNode;
  /** Which token set to render in — the value of `data-nl-design`. Farndale is
   *  the default because its palette IS the approved one; an app re-skins by
   *  defining its own block of tokens in the same shape, never by restyling a
   *  component. */
  theme?: string;
  /** Overrides "Ask Mira AI" where an app's assistant has another name. */
  assistantLabel?: string;
  /** Set when this app has no assistant wired yet — see AskMiraBar. */
  assistantUnavailable?: string;
}) {
  const [askOpen, setAskOpen] = useState(false);
  return (
    <div
      data-nl-design={theme}
      className="flex min-h-screen flex-col"
      style={{
        background: "var(--nl-bg)", color: "var(--nl-ink)",
        /* The shell states its own typeface rather than inheriting whatever an
         * app's root happens to set.
         *
         * Found at Stage 4, measured not guessed: Memorial Hall's app root
         * carries `fontFamily: var(--nl-font)` for `@nulife/booking-ui`'s
         * screens, which is a different stack with no Inter in it — so the
         * chassis's own type was being overridden by an ancestor and the home
         * screen rendered in the wrong face. The package's stylesheet sets this
         * on `body`, which is correct and was not enough.
         *
         * Guardrail 2 is that consuming the package is SUFFICIENT. A shell
         * whose typography depends on what an app does above it is a
         * configuration step with a trap in it, which is the class of bug the
         * white gutters came from. Same value the stylesheet gives `body`, so
         * Farndale renders identically — the Stage 1 gate proves that. */
        fontFamily: "var(--font-sans)",
      }}
    >
      {banner}
      {(onHome || onBack) && (
        <header
          className="flex items-center gap-3 px-4 py-2.5"
          style={{ background: "var(--nl-bg-raised)", borderBottom: "1px solid var(--nl-stroke)" }}
        >
          {onBack && (
            <button
              type="button" onClick={onBack}
              className="text-sm uppercase tracking-wide"
              style={{
                color: "var(--nl-ink)", background: "none", border: 0,
                cursor: "pointer", minHeight: "var(--nl-hit-min)",
              }}
            >‹ Back</button>
          )}
          {onHome && (
            <button
              type="button" onClick={onHome}
              className="text-sm uppercase tracking-wide"
              style={{
                color: "var(--nl-ink-dim)", background: "none", border: 0,
                cursor: "pointer", minHeight: "var(--nl-hit-min)",
              }}
            >{onBack ? "Home" : "‹ Home"}</button>
          )}
          {title && <span className="text-lg tracking-wide">{title}</span>}
        </header>
      )}
      {hero}
      {pulse}
      <main className="flex-1 overflow-y-auto px-4 py-4">{children}</main>
      {modules}
      {updateBanner}
      <AskMiraBar
        onOpen={() => setAskOpen(true)}
        label={assistantLabel}
        unavailableReason={assistantUnavailable}
      />
      {/* Never mounted when there is nothing behind it: a panel that opens and
          cannot answer is worse than a bar that says it is not connected. */}
      {!assistantUnavailable && (
        <AskMiraPanel
          open={askOpen}
          onClose={() => setAskOpen(false)}
          onSubmit={(t, m) => onAskMira?.(t, m)}
          voiceAvailable={voiceAvailable}
        />
      )}
    </div>
  );
}
